import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Plus, TrendingUp, TrendingDown, Calendar, DollarSign, Clock, Download, Filter, Upload, Ship, Link2, BarChart3, PlusCircle } from 'lucide-react';
import { Dialog } from 'primereact/dialog';
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import ReusableForm from '../common/ReusableForm';
import ReusableTable from '../dashboard/ReusableTable';
import jsPDF from 'jspdf';
import { getRequest, postRequest, putRequest, deleteRequest, formatINRCrore } from '../services/apiService';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import StageBudgetAllocationCard from './StageBudgetAllocationCard';
import FinanceSummaryCharts from '../dashboard/FinanceSummaryCharts';

const FinanceManagement = () => {
  const toast = useRef(null);
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('2024-2025');
  const [selectedQuarter, setSelectedQuarter] = useState('q1');
  const [transactions, setTransactions] = useState([]);
  const [editTransaction, setEditTransaction] = useState(null);
  const [deleteTransaction, setDeleteTransaction] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [formData, setFormData] = useState({
    project: 1,
    ship: [],
    total_budget: 1000000,
    shipBudgets: {},
    remarks: '',
    autoDistribute: false
  });
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartError, setChartError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [shipLoading, setShipLoading] = useState(false);
  const [showSelectionPopup, setShowSelectionPopup] = useState(false);
  const [activeSubPopup, setActiveSubPopup] = useState(null);

  const fetchData = useCallback(async () => {
    
    setLoading(true);
    try {
      const transactionsResponse = await getRequest(`/transaction/finances/`);
      
      if (transactionsResponse && Array.isArray(transactionsResponse)) {
        setTransactions(transactionsResponse);
        setFinancialData(null);
      } else if (transactionsResponse?.data && Array.isArray(transactionsResponse.data)) {
         setTransactions(transactionsResponse.data);
      } else {
        setTransactions([]);
        setFinancialData(null);
      }
    } catch (transactionErr) {
      setTransactions([]);
      setFinancialData(null);
      if (toast.current) {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to fetch transaction data.', life: 3000 });
      }
    } finally {
      setLoading(false);
    }
  }, [selectedYear, selectedQuarter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const transactionColumns = [
    {
      field: 'ship.name',
      header: 'Ship',
      body: (rowData) => rowData.ship && rowData.ship.name ? rowData.ship.name : 'N/A'
    },
    {
      field: 'total_budget',
      header: 'Total Budget',
      body: (rowData) => `₹ ${formatINRCrore(rowData.total_budget)}Cr`
    },
    {
      field: 'fixed_budget',
      header: 'Fixed Budget',
      body: (rowData) => `₹ ${formatINRCrore(rowData.fixed_budget)}Cr`
    },
    {
      field: 'variable_budget',
      header: 'Variable Budget',
      body: (rowData) => `₹ ${formatINRCrore(rowData.variable_budget)}Cr`
    },
    {
      field: 'ship.shipyard.code',
      header: 'Ship Yard',
      body: (rowData) => rowData.ship && rowData.ship.shipyard && rowData.ship.shipyard.code ? rowData.ship.shipyard.code : 'N/A'
    },
    {
      field: 'created_on',
      header: 'Created On',
      body: (rowData) => new Date(rowData.created_on).toLocaleDateString()
    },
    {
      field: 'status_db',
      header: 'Status',
      body: (rowData) => (
        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${rowData.status_db === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}>
          {rowData.status_db === 1 ? 'Active' : 'Inactive'}
        </span>
      )
    },
    
  ];

  const years = ['2023-2024', '2024-2025', '2025-2026'];
  const quarters = ['q1', 'q2', 'q3', 'q4'];


  useEffect(() => {
    getRequest('/master/projects/').then(res => {
      setProjects(res?.data || []);
    });
  }, []);

  useEffect(() => {
    if (!formData.project) {
      setShips([]);
      setFormData(prev => ({ ...prev, ship: [] }));
      return;
    }
    setShipLoading(true);
    getRequest(`/master/projects/?parent_id=${formData.project}`)
      .then(res => setShips(res?.data || []))
      .finally(() => setShipLoading(false));
  }, [formData.project,]);

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
    }
    
    setLoading(true);
    try {
      const endpoint = editTransaction
        ? `/transaction/finances/${editTransaction.id}/`
        : '/transaction/finances/';
  
      const response = await (editTransaction
        ? putRequest(endpoint, formData)
        : postRequest(endpoint, formData));
  
      if (response?.status === 'success' || response?.id || (response && !response.error)) {
        if (toast.current) {
          toast.current.show({  severity: 'success', summary: 'Success', detail: `Transaction ${editTransaction ? 'updated' : 'added'} successfully!`, life: 3000 });
        }
  
        await fetchData();
        setShowTransactionModal(false);
        setEditTransaction(null);
        setFormData({
          project: '',
          ship: [],
          total_budget: 1000000,
          shipBudgets: {},
          remarks: '',
          autoDistribute: false
        });
  
      } else {
        if (toast.current) {
          toast.current.show({  severity: 'error', summary: 'Error', detail: response?.message || response?.error || 'Failed to submit transaction.', life: 3000 });
        }
      }
  
    } catch (err) {
      if (toast.current) {
        toast.current.show({  severity: 'error', summary: 'Error', detail: err.message || 'An unexpected error occurred.', life: 3000 });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTransaction) return;

    setLoading(true);
    try {
      await deleteRequest(`/transaction/finances/${deleteTransaction.id}/`);
      if (toast.current) {
        toast.current.show({ severity: 'success', summary: 'Deleted',   detail: 'Transaction deleted successfully.',life: 3000   });
      }
      await fetchData();
      setDeleteTransaction(null);
    } catch (err) {
      if (toast.current) {
        toast.current.show({  severity: 'error', summary: 'Error', detail: err.message || 'Failed to delete transaction.', life: 3000 });
      }
    } finally {
      setLoading(false);
      setDeleteTransaction(null);
    }
  };




  const formFields = [
    {
      name: 'project',
      label: 'Project',
      type: 'select',
      options: projects.map(p => ({ label: p.name, value: p.id })),
      required: true
    },
    {
      name: 'ship',
      label: 'Ship',
      type: 'multiselect',
      options: ships.map(s => ({ label: s.name, value: s.id })),
      required: true,
      disabled: !formData.project || shipLoading
    },
    {
      name: 'financial_year',
      label: 'Financial Year',
      type: 'select',
      options: years.map(year => ({ label: year, value: year })),
      required: true
    },
    {
      name: 'quarter',
      label: 'Quarter',
      type: 'select',
      options: quarters.map(q => ({ label: q.toUpperCase(), value: q })),
      required: true
    },
    {
      name: 'mdl_variable_amount',
      label: 'MDL Variable Amount (₹)',
      type: 'number',
      required: true,
      step: "0.01"
    },
    {
      name: 'mdl_fixed_amount',
      label: 'MDL Fixed Amount (₹)',
      type: 'number',
      required: true,
      step: "0.01"
    },
    {
      name: 'grse_variable_amount',
      label: 'GRSE Variable Amount (₹)',
      type: 'number',
      required: true,
      step: "0.01"
    },
    {
      name: 'grse_fixed_amount',
      label: 'GRSE Fixed Amount (₹)',
      type: 'number',
      required: true,
      step: "0.01"
    }
  ];

  useEffect(() => {
    console.log('[State Update] chartData state changed to:', chartData);
  }, [chartData]);

  const handleOpenSubPopup = (popupId) => {
    setShowSelectionPopup(false);
    setActiveSubPopup(popupId);
  };

  useEffect(() => {
    if (activeSubPopup === 'popup1'){
      setFormData({
        project: '',
        ship: [],
        total_budget: 1000000,
        shipBudgets: {},
        remarks: '',
        autoDistribute: false
      });
    }
  }, [activeSubPopup]);

  const handleEdit = (row) => {
    setEditTransaction(row);
    setShowSelectionPopup(true);
    // setShowTransactionModal(true);
  };



  // if (loading && !financialData) {
  //   return (
  //     <div className="flex justify-center items-center h-screen">
  //       <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
  //     </div>
  //   );
  // }

  // if (error && !financialData) {
  //   return (
  //     <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4" role="alert">
  //       <p className="font-bold">Error</p>
  //       <p>{error}</p>
  //   </div>
  // );
  // }


  return (
    <div className="bg-gradient-to-br from-indigo-50 to-blue-50 min-h-screen p-4">
      <Toast ref={toast} />
      <Dialog
        visible={showTransactionModal}
        headerClassName="bg-indigo-800 text-white px-6 py-4 rounded-t-lg"
        header={editTransaction ? "Edit Transaction" : "Add New Transaction"}
        className="max-w-3xl w-full"
        onHide={() => {
          setShowTransactionModal(false);
          setShowSelectionPopup(true);
          setEditTransaction(null);
          setFormData({
            project: '',
            ship: [],
            total_budget: 1000000,
            shipBudgets: {},
            remarks: '',
            autoDistribute: false
          });
        }}
      >
        <ReusableForm
          formData={formData}
          setFormData={setFormData}
          fields={formFields}
          onSubmit={handleSubmit}
          onHide={() => setShowTransactionModal(false)}
          isLoading={loading}
        />
      </Dialog>

     

      <Dialog
        visible={showSelectionPopup}
        header="Project Management"
        headerClassName="bg-[#1e3a8a] text-white rounded-t-lg"
        className="max-w-5xl w-full bg-slate-500 "
        onHide={() => setShowSelectionPopup(false)}
        modal
      >
        <div className="p-8 mt-4 grid grid-cols-1 md:grid-cols-3 gap-8 bg-[#f6f9ff]">
          <div className="bg-white rounded-2xl shadow border-t-8 border-blue-500 flex flex-col items-center min-h-[220px] p-8 relative transition-transform transform hover:scale-105">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-blue-100 flex items-center justify-center rounded-full w-16 h-16 shadow-md">
                <Ship className="text-blue-500 w-8 h-8" />
              </div>
              <div className="absolute top-0 right-0 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">1</div>
            </div>
            <div className="mt-10 text-xl font-bold text-gray-800 text-center">Budget Allocation & Distribution</div>
            <div className="text-gray-500 text-center mb-4 mt-2">Allocate Budget and Distribution for Ships</div>
            <div className="mt-auto w-full">
              <Button
                label="Go to"
                icon="pi pi-arrow-right"
                className="p-button-sm p-2 p-button-raised bg-blue-600 text-white w-full transition-transform transform hover:scale-105"
                onClick={() => handleOpenSubPopup('popup1')}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow border-t-8 border-green-500 flex flex-col items-center min-h-[220px] p-8 relative transition-transform transform hover:scale-105">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-green-100 flex items-center justify-center rounded-full w-16 h-16 shadow-md">
                <Link2 className="text-green-500 w-8 h-8" />
              </div>
              <div className="absolute top-0 right-0 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">2</div>
            </div>
            
            <div className="mt-10 text-xl font-bold text-gray-800 text-center">Activity Mapping</div>
            <div className="text-gray-500 text-center mb-4 mt-2">Map and manage activities for projects</div>
            <div className="mt-auto w-full">
              <Button
                label="Go to"
                icon="pi pi-arrow-right"
                className="p-button-sm p-2 p-button-raised bg-blue-600 text-white w-full transition-transform transform hover:scale-105"
                onClick={() => handleOpenSubPopup('popup2')}
              />
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow border-t-8 border-yellow-500 flex flex-col items-center min-h-[220px] p-8 relative transition-transform transform hover:scale-105">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="bg-yellow-100 flex items-center justify-center rounded-full w-16 h-16 shadow-md">
                <BarChart3 className="text-yellow-500 w-8 h-8" />
              </div>
              <div className="absolute top-0 right-0 bg-yellow-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs">3</div>
            </div>
            <div className="mt-10 text-xl font-bold text-gray-800 text-center">Stage Budget Allocation</div>
            <div className="text-gray-500 text-center mb-4 mt-2">Allocate budgets for Ship</div>
            <div className="mt-auto w-full">
              <Button
                label="Go to"
                icon="pi pi-arrow-right"
                className="p-button-sm p-2 p-button-raised bg-blue-600 text-white w-full transition-transform transform hover:scale-105"
                onClick={() => handleOpenSubPopup('popup3')}
              />
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog visible={activeSubPopup === 'popup1'} header="Budget Allocation & Distribution" modal className="max-w-7xl w-full" 
      onHide={() => { setActiveSubPopup(null) }} headerClassName="bg-indigo-700 text-white px-6 py-4 rounded-t-lg">
        <ShipBudgetCard 
          setActiveSubPopup={setActiveSubPopup}
          setShowSelectionPopup={setShowSelectionPopup}
          editTransaction={editTransaction}
        />
      </Dialog>

      <Dialog
        visible={activeSubPopup === 'popup3'}
        header="Stage Budget Allocation"
        modal
        className="max-w-7xl w-full"
        onHide={() => {
          setActiveSubPopup(null);
          setShowSelectionPopup(true);
        }}
        headerClassName="bg-indigo-700 text-white px-6 py-4 rounded-t-lg"
      >
        <StageBudgetAllocationCard setActiveSubPopup={setActiveSubPopup} setShowSelectionPopup={setShowSelectionPopup} editTransaction={editTransaction} />
      </Dialog>

      <Dialog
        visible={activeSubPopup === 'popup2'}
        header="Activity Mapping"
        modal
        className="max-w-7xl w-full"
        onHide={() => {
          setActiveSubPopup(null);
          setShowSelectionPopup(true);
        }}
        headerClassName="bg-indigo-700 text-white  rounded-t-lg"
      >
        <ActivityMappingCard setActiveSubPopup={setActiveSubPopup} setShowSelectionPopup={setShowSelectionPopup} editTransaction={editTransaction} />
      </Dialog>

      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-indigo-900">Financial Management</h1>
            <p className="text-indigo-700">Track budgets, expenditures and financial reports</p>
          </div>
          <div className="flex mt-4 md:mt-0 space-x-2">
            <div className="relative">
              <select 
                className="appearance-none block w-full px-3 py-2 border border-indigo-300 rounded-lg shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 bg-white transition-all"
                value={formData.project}
                onChange={(e) => setFormData(prev => ({ ...prev, project: e.target.value }))}
                disabled={loading}
              >
                <option value="" disabled>Select Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>{project.name}</option>
                ))}
              </select>
              <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="w-5 h-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.23 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </span>
            </div>
           
            <button
              onClick={() => setShowSelectionPopup(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-md text-white bg-gradient-to-r from-purple-600 to-pink-700 hover:from-purple-700 hover:to-pink-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all disabled:opacity-50"
              disabled={loading}
            >
              <Filter className="mr-2 h-4 w-4" /> Financial Management Configuration
            </button>
          </div>
        </div>

        <div className="w-full bg-white p-4 rounded-lg shadow-md">
        <FinanceSummaryCharts
              selectedProject={1}
              selectedShip={12}
            />
        </div>
        <ReusableTable
            title="Transactions"
            data={transactions}
            setData={setTransactions}
            columnFields={transactionColumns}
            // setAddNewDialogVisible={setShowTransactionModal}
            actionMenu={true}
            actionItems={[
              {
                label: 'Edit',
                icon: 'pi pi-pencil',
                command: (row) => handleEdit(row)
              },
              {
                label: 'Delete',
                icon: 'pi pi-trash',
                command: (row) => setDeleteTransaction(row)
              }
            ]}
            loading={loading}
            handleDelete={handleDelete}
            colorScheme={{
              headerBgColor: '#1e40af',
              headerTextColor: '#ffffff',
              tableBgColor: '#f3f4f6',
              tableTextColor: '#1f2937',
            }}
          />
      </div>
    </div>
  );
};

export default FinanceManagement;

const ShipBudgetCard = ({ setActiveSubPopup, setShowSelectionPopup, editTransaction }) => {
  const toast = useRef(null);
  const [formData, setFormData] = useState({
    project: '',
    ship: [],
    total_budget: 1000000,
    shipBudgets: {},
    remarks: '',
    autoDistribute: false
  });
  const [projects, setProjects] = useState([]);
  const [ships, setShips] = useState([]);
  const [shipLoading, setShipLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch projects on component mount
    getRequest('/master/projects/').then(res => {
      setProjects(res?.data || []);
    });
  }, []);

  useEffect(() => {
    if (editTransaction) {
      console.log(editTransaction);
      getRequest(`/master/projects/?parent_id=${editTransaction.project.id}`)
        .then(res => {
          setShips(res?.data || []);
          let shipBudgets = {};
          if (editTransaction.ships && Array.isArray(editTransaction.ships)) {
            editTransaction.ships.forEach(shipObj => {
              shipBudgets[shipObj.id] = {
                shipBudget: shipObj.total_budget,
                varablePercentage: shipObj.total_budget           
                ? (Number(shipObj.variable_budget) /
                   Number(shipObj.total_budget)) * 100
                : 0,
                variableBudget: shipObj.variable_budget || 0,
                fixedBudget: shipObj.fixed_budget || 0
              };
            });
          } else if (editTransaction.ship) {
            shipBudgets[editTransaction.ship.id] = {
              shipBudget: editTransaction.total_budget,
              varablePercentage: editTransaction.total_budget           
                ? (Number(editTransaction.variable_budget) /
                   Number(editTransaction.total_budget)) * 100
                : 0,
              variableBudget: editTransaction.variable_budget || 0,
              fixedBudget: editTransaction.fixed_budget || 0
            };
          }
          setFormData({
            id: editTransaction.id,
            project: editTransaction.project.id,
            ship: editTransaction.ships
              ? editTransaction.ships.map(s => s.id)
              : [editTransaction.ship.id],
            total_budget: editTransaction.project_budget,
            shipBudgets,
            remarks: editTransaction.remarks || '',
            autoDistribute: false
          });
        });
    }
  }, [editTransaction]);

  useEffect(() => {
    // Fetch ships when project is selected
    if (!formData.project) {
      setShips([]);
      setFormData(prev => ({ ...prev, ship: [] }));
      return;
    }
    setShipLoading(true);
    getRequest(`/master/projects/?parent_id=${formData.project}`)
      .then(res => setShips(res?.data || []))
      .finally(() => setShipLoading(false));
  }, [formData.project]);

  const handleShipBudgetFormChange = (e, field) => {
    if (field.name === 'ship') {
      const selectedShips = e.value || [];
      setFormData(prev => {
        const newShipBudgets = { ...prev.shipBudgets };
        // Remove budgets for unselected ships
        Object.keys(newShipBudgets).forEach(shipId => {
          if (!selectedShips.includes(shipId)) {
            delete newShipBudgets[shipId];
          }
        });
        // Initialize budgets for newly selected ships
        selectedShips.forEach(shipId => {
          if (!newShipBudgets[shipId]) {
            newShipBudgets[shipId] = {
              shipBudget: 0,
              varablePercentage: 0,
              variableBudget: 0,
              fixedBudget: 0
            };
          }
        });
        return {
          ...prev,
          ship: selectedShips,
          shipBudgets: newShipBudgets,
          autoDistribute: false // Reset auto distribute when ships change
        };
      });
    } else if (field.name === 'autoDistribute') {
      const checked = e.target.checked;
      if (checked && formData.ship.length > 0) {
        const equalBudget = formData.total_budget / formData.ship.length;
        const newShipBudgets = {};
        
        formData.ship.forEach(shipId => {
          newShipBudgets[shipId] = {
            shipBudget: equalBudget,
            varablePercentage: 0,
            variableBudget: 0,
            fixedBudget: equalBudget
          };
        });

        setFormData(prev => ({
          ...prev,
          autoDistribute: checked,
          shipBudgets: newShipBudgets
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          autoDistribute: checked
        }));
      }
    } else if (field.name.startsWith('ship_')) {
      const [_, shipId, fieldName] = field.name.split('_');
      const value = e.value || 0;

      setFormData(prev => {
        const newShipBudgets = { ...prev.shipBudgets };
        const currentShipBudget = newShipBudgets[shipId] || {
          shipBudget: 0,
          varablePercentage: 0,
          variableBudget: 0,
          fixedBudget: 0
        };

        if (fieldName === 'shipBudget') {
          const otherShipsTotal = Object.entries(newShipBudgets).reduce((sum, [id, budget]) => {
            return id === shipId ? sum : sum + (parseFloat(budget.shipBudget) || 0);
          }, 0);

          if (otherShipsTotal + value > prev.total_budget) {
            toast.current?.show({
              severity: 'error',
              summary: 'Error',
              detail: 'Total ship budgets cannot exceed total project budget',
              life: 3000
            });
            return prev;
          }

          const varablePercentage = parseFloat(currentShipBudget.varablePercentage || 0);
          const variableBudget = parseFloat(((varablePercentage / 100) * value).toFixed(2));
          const fixedBudget = parseFloat((value - variableBudget).toFixed(2));

          newShipBudgets[shipId] = {
            ...currentShipBudget,
            shipBudget: value,
            variableBudget,
            fixedBudget
          };
        } else if (fieldName === 'varablePercentage') {
          const shipBudget = parseFloat(currentShipBudget.shipBudget || 0);
          const varablePercentage = parseFloat(value || 0);

          const variableBudget = parseFloat(((varablePercentage / 100) * shipBudget).toFixed(2));
          const fixedBudget = parseFloat((shipBudget - variableBudget).toFixed(2));

          newShipBudgets[shipId] = {
            ...currentShipBudget,
            varablePercentage,
            variableBudget,
            fixedBudget
          };
        }

        return {
          ...prev,
          shipBudgets: newShipBudgets
        };
      });
    }
  };

  const handleShipBudgetFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    console.log(formData);
    try {
      const totalAllocated = Object.values(formData.shipBudgets || {}).reduce((sum, budget) =>
        sum + (parseFloat(budget.shipBudget) || 0), 0
      );

      if (totalAllocated > formData.total_budget) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Total allocated budget cannot exceed total project budget',
          life: 3000
        });
        return;
      }

      const hasEmptyFields = formData.ship.some(shipId => {
        const budget = formData.shipBudgets[shipId];
        return !budget ||
          budget.shipBudget === 0 ||
          budget.varablePercentage === 0;
      });

      if (hasEmptyFields) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Please fill all required fields for each ship',
          life: 3000
        });
        return;
      }

      const response = await postRequest("/transaction/finances/", formData);
      if (response.status === 'success') {
      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Ship budget submitted successfully',
        life: 3000
      });
      setFormData({
        project: '',
        ship: [],
        total_budget: 1000000,
        shipBudgets: {},
        remarks: '',
        autoDistribute: false
      });

      // Close dialog and show selection popup
      setActiveSubPopup(null);
      setShowSelectionPopup(true);
      }
    } catch (error) {
      console.error('Error submitting ship budget:', error);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error.message || 'Failed to submit ship budget',
        life: 3000
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Toast ref={toast} position="top-right" />
      <Button
              type="button"
              label="Back"
              icon="pi pi-arrow-left"
              className="bg-red-500 text-white px-4 py-2 my-2 rounded-md w-32"
              onClick={() => {
                setActiveSubPopup(null);
                setShowSelectionPopup(true);
              }}
            />
      <div className='bg-indigo-50 rounded-lg p-4 mb-2'>
        <div className="grid grid-cols-3 gap-5">
          <div className="bg-white p-4 rounded shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">Total Project Budget</p>
              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">100%</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">₹{parseFloat(formData.total_budget).toFixed(2)}Cr</p>
            <div className="mt-2 text-xs text-gray-500">
              Fiscal Year 2023-24
            </div>
          </div>
          <div className="bg-white p-4 rounded shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">Utilized Budget</p>
              <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">
                {((Object.values(formData.shipBudgets || {}).reduce((sum, budget) => sum + (parseFloat(budget.shipBudget) || 0), 0) / formData.total_budget) * 100).toFixed(2)}%
              </span>
            </div>
            <p className="text-xl font-semibold text-green-600">
              ₹{Object.values(formData.shipBudgets || {}).reduce((sum, budget) => sum + (parseFloat(budget.shipBudget) || 0), 0).toFixed(2)}Cr
            </p>
            <div className="mt-2 text-xs text-gray-500">
              Updated {new Date().toLocaleDateString()}
            </div>
          </div>
          <div className="bg-white p-4 rounded shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-gray-500">Remaining Budget</p>
              <span className="text-xs px-2 py-1 bg-orange-100 text-orange-800 rounded-full">
                {((formData.total_budget - Object.values(formData.shipBudgets || {}).reduce((sum, budget) => sum + (parseFloat(budget.shipBudget) || 0), 0)) / formData.total_budget * 100).toFixed(2)}%
              </span>
            </div>
            <p className="text-xl font-semibold text-orange-600">
              ₹{(formData.total_budget - Object.values(formData.shipBudgets || {}).reduce((sum, budget) => sum + (parseFloat(budget.shipBudget) || 0), 0)).toFixed(2)}Cr
            </p>
            <div className="mt-2 text-xs text-gray-500">
              Available Balance
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 bg-indigo-50">
        <form onSubmit={handleShipBudgetFormSubmit} className="">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="field">
              <label htmlFor="project" className="block text-sm font-medium text-gray-700 mb-1">Project</label>
              <Dropdown
                id="project"
                value={formData.project}
                options={projects.map(p => ({ label: p.name, value: p.id }))}
                onChange={(e) => setFormData(prev => ({ ...prev, project: e.value }))}
                placeholder="Select Project"
                className="w-full"
                required
              />
            </div>

            <div className="field">
              <label htmlFor="ships" className="block text-sm font-medium text-gray-700 mb-1">Ships</label>
              <MultiSelect
                id="ships"
                value={formData.ship}
                options={ships.map(s => ({ label: s.name, value: s.id }))}
                onChange={(e) => handleShipBudgetFormChange(e, { name: 'ship' })}
                placeholder="Select Ships"
                className="w-full "
                required
                disabled={!formData.project || shipLoading}
                filter
              />
            </div>

            <div className="field">
              <label htmlFor="totalBudget" className="block text-sm font-medium text-gray-700 mb-1">Total Budget (in Cr)</label>
              <InputNumber
                id="totalBudget"
                value={formData.total_budget}
                onChange={(e) => setFormData(prev => ({ ...prev, total_budget: e.value }))}
                mode="currency"
                currency="INR"
                locale="en-IN"
                className="w-full"
              />
            </div>
          </div>
          <div className="field flex items-center ">
              <div className="flex flex-col">
                {!formData.ship.length ? (
                  <div className="bg-yellow-100 py-3 px-2 mt-2 rounded-md border border-yellow-300 text-xs text-gray-500 w-full">
                    <i className="pi pi-info-circle mr-2"></i> Select ships to enable auto distribute budget on Selected Ships
                  </div>
                ) : (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="autoDistribute"
                      checked={formData.autoDistribute}
                      onChange={(e) => handleShipBudgetFormChange(e, { name: 'autoDistribute' })}
                      className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                    />
                    <label 
                      htmlFor="autoDistribute" 
                      className="ml-2 text-sm text-gray-700"
                    >
                      Auto Distribute Budget in Ships
                    </label>
                  </div>
                )}
              </div>
            </div>
          <div className="grid grid-cols-1 gap-6">
            {formData.ship.map(shipId => {
              const ship = ships.find(s => s.id === shipId);
              const shipBudget = formData.shipBudgets[shipId] || {};
              if (!ship) return null;

              return (
                <Card key={shipId} className="mb-4 bg-green-50">
                  <template title>
                    <div className="flex items-center">
                      <i className="pi pi-ship mr-2"></i>
                      <span className="text-lg font-medium">{ship.name}</span>
                    </div>
                  </template>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="field">
                      <label htmlFor={`ship_${shipId}_budget`} className="block text-sm font-medium text-gray-700 mb-1">Ship Budget (in Cr)</label>
                      <InputNumber
                        id={`ship_${shipId}_budget`}
                        name={`ship_${shipId}_shipBudget`}
                        value={shipBudget.shipBudget}
                        onValueChange={(e) => handleShipBudgetFormChange(e, { name: `ship_${shipId}_shipBudget` })}
                        mode="currency"
                        currency="INR"
                        locale="en-IN"
                        className="w-full"
                        required
                        min={0}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`ship_${shipId}_percentage`} className="block text-sm font-medium text-gray-700 mb-1">Variable %</label>
                      <InputNumber
                        id={`ship_${shipId}_percentage`}
                        name={`ship_${shipId}_varablePercentage`}
                        value={shipBudget.varablePercentage}
                        onValueChange={(e) => handleShipBudgetFormChange(e, { name: `ship_${shipId}_varablePercentage` })}
                        suffix="%"
                        min={0}
                        max={100}
                        className="w-full"
                        required
                        useGrouping={false}
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`ship_${shipId}_variable`} className="block text-sm font-medium text-gray-700 mb-1">Variable Budget (in Cr)</label>
                      <InputNumber
                        id={`ship_${shipId}_variable`}
                        value={shipBudget.variableBudget}
                        disabled
                        mode="currency"
                        currency="INR"
                        locale="en-IN"
                        className="w-full"
                      />
                    </div>
                    <div className="field">
                      <label htmlFor={`ship_${shipId}_fixed`} className="block text-sm font-medium text-gray-700 mb-1">Fixed Budget (in Cr)</label>
                      <InputNumber
                        id={`ship_${shipId}_fixed`}
                        value={shipBudget.fixedBudget}
                        disabled
                        mode="currency"
                        currency="INR"
                        locale="en-IN"
                        className="w-full"
                      />
                    </div>
                  </div>
                </Card>
              );
            })}
            <div className="field">
              <label htmlFor="remarks" className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
              <InputTextarea
                id="remarks"
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                rows={4}
                className="w-full"
                autoResize
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
           
            <Button
              type="submit"
              label={loading ? 'Saving...' : 'Save'}
              icon="pi pi-check"
              loading={loading}
              className="bg-indigo-700 text-white px-4 py-2 rounded-md w-32"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

// import React, { useState, useEffect } from 'react';

const ActivityMappingCard = ({ setActiveSubPopup, setShowSelectionPopup, editTransaction }) => {
  /* ------------------------------------------------------------------ */
  /*  STATE                                                             */
  /* ------------------------------------------------------------------ */
  const [activities,         setActivities]        = useState([]);
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [selectedStage,      setSelectedStage]     = useState('');
  const [searchQuery,        setSearchQuery]       = useState('');
  const [selectedActivities, setSelectedActivities] = useState({});
  const [currentPage,        setCurrentPage]       = useState(1);
  const [loading,            setLoading]           = useState(true);
  const [activeFilter,       setActiveFilter]      = useState('all');
  const [stages,             setStages]            = useState([]);
  const [counts,             setCounts]            = useState({
    total: 0,
    selected: 0,
    unselected: 0,
  });

  const itemsPerPage = 24;

  /* ------------------------------------------------------------------ */
  /*  API CALLS                                                         */
  /* ------------------------------------------------------------------ */
  const fetchStages = async () => {
    try {
      const response = await getRequest('/test/task-transaction-api/?project_id=12&task_type=milestone&milestone__milestone_type=payment');
      if (response && response.data) {
        // Sort stages by their title to maintain order
        const sortedStages = response.data.sort((a, b) => {
          const aNum = parseInt(a.milestone.title.match(/\d+/)?.[0] || '0');
          const bNum = parseInt(b.milestone.title.match(/\d+/)?.[0] || '0');
          return aNum - bNum;
        });
        setStages(sortedStages);
      }
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await getRequest(`/test/task-transaction-api/?project_id=12${searchQuery ? `&search=${searchQuery}` : ''}`);
      if (response && response.data) {
        const formattedActivities = response.data.map(activity => ({
          id: activity.id,
          name: activity.name,
          stage: activity.milestone?.title || 'Unassigned',
          priority: activity.is_completed ? 'Completed' : 'In Progress',
          category: activity.task_type,
          description: activity.remarks || '',
          startDate: activity.start_date,
          endDate: activity.end_date,
          actualStart: activity.actual_start,
          actualFinish: activity.actual_finish,
          plannedPercent: activity.user_planned_percent,
          actualPercent: activity.actual_completion_percent,
          budget: activity.ship_budget,
          fixedBudget: activity.ship_fixed_budget,
          variableBudget: activity.ship_variable_budget
        }));

        // Filter activities based on selected stage if one is selected
        const filteredActivities = selectedStage 
          ? formattedActivities.filter(activity => activity.stage === selectedStage)
          : formattedActivities;

        setActivities(filteredActivities);
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  EFFECTS                                                           */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    fetchStages();
    fetchActivities();
  }, []);

  useEffect(() => {
    if (selectedStage) {
      fetchActivities();
    }
  }, [selectedStage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchActivities();
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  /* ------------------------------------------------------------------ */
  /*  APPLY SEARCH + UPDATE COUNTS + FILTER BY TAB                      */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    let base = activities;
    
    /* 2. counts before tab filter */
    const sel = base.filter(a => selectedActivities[a.id]).length;
    const tot = base.length;
    setCounts({ total: tot, selected: sel, unselected: tot - sel });

    /* 3. tab filter */
    let list = base;
    if (activeFilter === 'selected')    list = base.filter(a => selectedActivities[a.id]);
    if (activeFilter === 'unselected')  list = base.filter(a => !selectedActivities[a.id]);

    setFilteredActivities(list);
    setCurrentPage(1);
  }, [activities, selectedActivities, activeFilter]);

  /* ------------------------------------------------------------------ */
  /*  SELECTION HELPERS                                                 */
  /* ------------------------------------------------------------------ */
  const handleCheckboxChange = id =>
    setSelectedActivities(prev => ({ ...prev, [id]: !prev[id] }));

  const selectAllVisible = () =>
    setSelectedActivities(prev => {
      const upd = { ...prev };
      currentActivities.forEach(a => { upd[a.id] = true; });
      return upd;
    });

  const deselectAllVisible = () =>
    setSelectedActivities(prev => {
      const upd = { ...prev };
      currentActivities.forEach(a => { delete upd[a.id]; });
      return upd;
    });

  /* ------------------------------------------------------------------ */
  /*  PAGINATION                                                        */
  /* ------------------------------------------------------------------ */
  const totalPages  = Math.max(1, Math.ceil(filteredActivities.length / itemsPerPage));
  const startIndex  = (currentPage - 1) * itemsPerPage;
  const endIndex    = Math.min(startIndex + itemsPerPage, filteredActivities.length);
  const currentActivities = filteredActivities.slice(startIndex, endIndex);

  /* arrange rows of 3 for grid */
  const activityRows = [];
  for (let i = 0; i < currentActivities.length; i += 3)
    activityRows.push(currentActivities.slice(i, i + 3));

  /* ------------------------------------------------------------------ */
  /*  FILTER CARD DATA                                                  */
  /* ------------------------------------------------------------------ */
  const filters = {
    all:        { label: 'All Activities', count: counts.total,       icon: '📋' },
    selected:   { label: 'Selected',       count: counts.selected,    icon: '✓'  },
    unselected: { label: 'Unselected',     count: counts.unselected,  icon: '◯'  },
  };

  return (
    <div className="mx-auto p-4 bg-indigo-50 rounded-lg shadow">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border rounded p-4">
          <label className="block text-sm font-medium mb-2">Select Stage</label>
          <select
            value={selectedStage}
            onChange={e => setSelectedStage(e.target.value)}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Stages</option>
            {stages.map(stage => (
              <option key={stage.id} value={stage.milestone.title}>{stage.milestone.title}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2 bg-white border rounded p-4">
          <label className="block text-sm font-medium mb-2">Search Activities</label>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by activity name"
              className="w-full p-2 pl-10 border rounded focus:ring-2 focus:ring-blue-500"
            />
            <span className="absolute left-3 top-2.5 text-gray-400">🔍</span>
          </div>
        </div>
      </div>

      {/* ------------------- filter cards --------------------------- */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {Object.entries(filters).map(([key, f]) => (
          <div
            key={key}
            onClick={() => setActiveFilter(key)}
            className={`cursor-pointer p-4 border rounded transition shadow-sm ${
              activeFilter === key
                ? 'bg-blue-50 border-blue-500'
                : 'bg-white border-gray-200 hover:border-blue-200'
            }`}
          >
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-2">
                <span className="text-xl">{f.icon}</span>
                <span className={`font-medium ${
                  activeFilter === key ? 'text-blue-700' : 'text-gray-700'
                }`}>{f.label}</span>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                activeFilter === key ? 'bg-blue-100 text-blue-700'
                                     : 'bg-gray-100 text-gray-700'
              }`}>{f.count}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ------------------- activity grid container ---------------- */}
      <div className="bg-white border rounded-lg p-4 shadow-sm">
        {/* top bar */}
        <div className="flex justify-between items-center mb-4 bg-gray-50 p-3 rounded border">
          <span className="text-sm text-gray-600">
            Showing {startIndex + 1}-{endIndex} of {filteredActivities.length}
          </span>
          <div className="space-x-2">
            <button onClick={selectAllVisible}
                    className="px-3 py-1.5 text-sm bg-blue-50 border border-blue-200 rounded
                               text-blue-600 hover:bg-blue-100">Select All</button>
            <button onClick={deselectAllVisible}
                    className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded
                               text-gray-600 hover:bg-gray-100">Deselect All</button>
          </div>
        </div>

        {/* grid / states */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full" />
          </div>
        ) : filteredActivities.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-gray-500">
            {selectedStage ? 'No activities found' : 'Please select a stage'}
          </div>
        ) : (
          <>
            {/* grid rows */}
            {activityRows.map((row, idx) => (
              <div key={idx} className="flex flex-wrap border-b py-2 hover:bg-gray-50">
                {row.map(a => (
                  <div key={a.id} className="w-full md:w-1/3 p-2">
                    <label className="flex items-center space-x-3 p-2 rounded cursor-pointer hover:bg-blue-50/20">
                      <input
                        type="checkbox"
                        checked={!!selectedActivities[a.id]}
                        onChange={() => handleCheckboxChange(a.id)}
                        className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div>
                        <span className="text-sm font-medium">{a.name}</span>
                        <div className="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                          <span className={`w-2 h-2 rounded-full ${
                            a.priority === 'High'   ? 'bg-red-500'   :
                            a.priority === 'Medium' ? 'bg-yellow-500': 'bg-green-500'
                          }`} />
                          <span>{a.category} • {a.priority}</span>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            ))}

            {/* pagination */}
            <div className="flex justify-center items-center gap-3 pt-4 border-t">
              <button onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                      disabled={currentPage === 1}
                      className={`p-2 rounded-full ${
                        currentPage === 1
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}>←</button>

              <span className="text-sm font-medium">Page {currentPage} of {totalPages}</span>

              <button onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`p-2 rounded-full ${
                        currentPage === totalPages
                          ? 'text-gray-300 cursor-not-allowed'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}>→</button>
            </div>
          </>
        )}
      </div>

      {/* ------------------- summary & actions ----------------------- */}
      <div className="mt-6 p-4 bg-blue-50 border rounded-lg shadow-sm">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center mb-3 md:mb-0">
            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
              <span className="text-blue-600 font-bold">
                {counts.selected}
              </span>
            </div>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Selected activities</span> of {activities.length} total
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              label="Back"
              icon="pi pi-arrow-left"
              className="bg-red-500 text-white rounded-md px-4 py-2"
              onClick={() => {
                setActiveSubPopup(null);
                setShowSelectionPopup(true);
              }}
            />
            <Button
              label="Save Activity Selected"
              icon="pi pi-save"
              className="bg-blue-500 text-white rounded-md px-4 py-2"
              onClick={() => {
                alert(`Saved ${counts.selected} activities for ${selectedStage}`);
                console.log(selectedActivities);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};


